import { Alert, Button, Group, Stack, Text, TextInput } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ErrorState,
  InlineError,
  LoadingSkeleton,
  NumField,
  SectionCard,
  StickyActionBar,
} from "../../components/ui";
import { api, type OutstandingLine, type PreOrderRef } from "../../lib/api";
import { Page } from "../../shell/Page";

/**
 * Confirming a day order (REQ-SALES-2, Q1).
 *
 * The Billing Executive is the first person in the whole flow to see stock,
 * and this is the screen where the decision is made: supply what is there,
 * and whatever is left stays outstanding on the day order rather than the
 * original request being edited down.
 *
 * No prices are sent. The server resolves each one from the Store Manager's
 * price history (Q7), so nothing typed here can change what is charged - only
 * how much is supplied.
 */
export function OrderConfirm() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const [preOrder, setPreOrder] = useState<PreOrderRef>();
  const [lines, setLines] = useState<OutstandingLine[]>();
  const [quantities, setQuantities] = useState<Record<string, number | string>>({});
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      setError(undefined);
      const loaded = await api.preOrderOutstanding(id);
      setPreOrder(loaded.preOrder);
      setLines(loaded.lines);
      // Default to the most that can actually be supplied: the outstanding
      // quantity, capped at what is on the shelf. That is the answer in the
      // common case, and it is visibly editable when it is not.
      setQuantities(
        Object.fromEntries(
          loaded.lines.map((line) => [
            line.productId,
            Math.max(0, Math.min(line.quantityOutstanding, line.currentStockQty)),
          ]),
        ),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the day order.");
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function quantityFor(line: OutstandingLine): number {
    const parsed = Number(quantities[line.productId] ?? 0);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
  }

  /** Why a line cannot be confirmed at the quantity typed, if it cannot. */
  function problemWith(line: OutstandingLine): string | undefined {
    const wanted = quantityFor(line);
    if (wanted > line.quantityOutstanding) {
      return `Only ${line.quantityOutstanding} outstanding`;
    }
    if (wanted > line.currentStockQty) {
      return `Only ${line.currentStockQty} in stock`;
    }
    return undefined;
  }

  const confirmable = (lines ?? []).filter((line) => quantityFor(line) > 0);
  const problems = (lines ?? []).filter((line) => problemWith(line) !== undefined);
  const canSubmit = confirmable.length > 0 && problems.length === 0 && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(undefined);
    try {
      const { order } = await api.createOrder({
        preOrderId: id,
        lineItems: confirmable.map((line) => ({
          productId: line.productId,
          quantityFulfilled: quantityFor(line),
        })),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      navigate(`/sales/bill/${order.id}`, { replace: true });
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Could not confirm the order. Nothing was saved.",
      );
      setBusy(false);
    }
  }

  const shortfall = (lines ?? []).reduce(
    (total, line) => total + Math.max(0, line.quantityOutstanding - quantityFor(line)),
    0,
  );

  return (
    <Page
      title="Confirm order"
      subtitle={preOrder ? `${preOrder.reference} · ${preOrder.customer.name}` : undefined}
      back="/sales/queue"
    >
      {error && !lines && <ErrorState message={error} onRetry={() => void load()} />}
      {error && lines && <InlineError>{error}</InlineError>}
      {!lines && !error && <LoadingSkeleton variant="card" count={2} />}

      {lines?.map((line) => {
        const problem = problemWith(line);
        return (
          <SectionCard key={line.productId} title={line.sku}>
            <Stack gap="sm">
              <Text fz="lg" fw={600} c="gray.9">
                {line.name}
              </Text>

              <Stack gap={6} component="dl" m={0}>
                <Count label="Asked for" value={line.quantityRequested} />
                {line.quantityFulfilled > 0 && (
                  <Count label="Already supplied" value={line.quantityFulfilled} />
                )}
                <Count label="Outstanding" value={line.quantityOutstanding} />
                <Count
                  label="In stock now"
                  value={line.currentStockQty}
                  // A word, not just a colour: "short" is the fact, and the
                  // red is only there to help find it.
                  note={
                    line.currentStockQty < line.quantityOutstanding ? "short" : undefined
                  }
                />
              </Stack>

              <NumField
                id={`qty-${line.productId}`}
                label="Supplying now"
                min={0}
                max={Math.min(line.quantityOutstanding, line.currentStockQty)}
                value={quantities[line.productId] ?? ""}
                onChange={(value) =>
                  setQuantities((current) => ({ ...current, [line.productId]: value }))
                }
                error={problem}
                description={
                  problem
                    ? undefined
                    : "Enter 0 to supply none of this line — it stays outstanding."
                }
              />
            </Stack>
          </SectionCard>
        );
      })}

      {lines && (
        <SectionCard title="Before you confirm">
          <Stack gap="sm">
            <TextInput
              id="order-notes"
              label="Note"
              description="Optional. Anything the delivery or billing needs to know."
              value={notes}
              maxLength={1000}
              onChange={(event) => setNotes(event.currentTarget.value)}
            />

            {shortfall > 0 && (
              <Alert
                variant="light"
                color="yellow"
                radius="md"
                icon={<IconAlertTriangle size={20} stroke={1.8} />}
              >
                {shortfall} unit{shortfall === 1 ? "" : "s"} will stay outstanding
                on {preOrder?.reference}. The day order remains in the queue.
              </Alert>
            )}
            {confirmable.length === 0 && (
              <Text fz="sm" c="gray.6">
                Set a quantity on at least one line to confirm.
              </Text>
            )}
            <Text fz="sm" c="gray.6">
              Confirming does not move stock. You will be taken straight to
              billing, which is the step that does.
            </Text>
          </Stack>
        </SectionCard>
      )}

      {lines && (
        <StickyActionBar
          context={
            confirmable.length > 0
              ? `${confirmable.reduce((n, line) => n + quantityFor(line), 0)} unit(s) on ${
                  confirmable.length
                } line(s)`
              : undefined
          }
          primary={
            <Button
              size="md"
              fullWidth
              disabled={!canSubmit}
              loading={busy}
              onClick={() => void submit()}
            >
              Confirm order
            </Button>
          }
        />
      )}
    </Page>
  );
}

function Count({
  label,
  value,
  note,
}: {
  label: string;
  value: number;
  note?: string;
}) {
  return (
    <Group justify="space-between" gap="md" wrap="nowrap">
      <Text component="dt" fz="sm" c="gray.6">
        {label}
      </Text>
      <Group component="dd" gap={6} m={0} wrap="nowrap">
        <Text fz="sm" fw={500} c="gray.9" className="num">
          {value}
        </Text>
        {note && (
          <Text fz="xs" fw={700} c="red.7" tt="uppercase">
            {note}
          </Text>
        )}
      </Group>
    </Group>
  );
}
